import type { JSX } from 'react';
import { portalSlideIn, PortalSlideInProps } from './PortalSlidein';
import { Clickable } from 'dg-web-shared/ui/Clickable';
import { ItemBlockGroup } from './ItemBlock';
import { css } from '@emotion/css';
import { Colors } from 'dg-web-shared/ui/vars';
import { Typo } from 'dg-web-shared/ui/typo.ts';
import { defaultTransition } from 'dg-web-shared/tb-ui/transitions.ts';
import { hover } from 'dg-web-shared/tb-ui/hover.ts';

export interface DropdownSlideInOption<T extends string | number | null> {
    id: T;
    display: React.ReactNode;
}

export interface DropdownSlideInProps<T extends string | number | null> {
    selected: T | null;
    options: DropdownSlideInOption<T>[];
    optionsHeader?: React.ReactNode;
    topOptions?: DropdownSlideInOption<T>[];
    topOptionsHeader?: React.ReactNode;
    onSelected: (id: T) => void;
    additionalContent?: React.ReactNode;
}

const DropdownSlideInContentComponent = <T extends string | number | null>(
    p: DropdownSlideInProps<T>,
) => (
    <>
        {!!p.topOptions && (
            <ItemBlockGroup
                title={p.topOptionsHeader}
                hasTopSpace={!p.topOptionsHeader}
            >
                <ItemsBlock
                    options={p.topOptions}
                    selected={p.selected}
                    onSelected={p.onSelected}
                />
            </ItemBlockGroup>
        )}
        <ItemBlockGroup title={p.optionsHeader} hasTopSpace={!p.optionsHeader}>
            <ItemsBlock
                options={p.options}
                selected={p.selected}
                onSelected={p.onSelected}
            />
        </ItemBlockGroup>
        {p.additionalContent}
    </>
);

const DropdownSlideInComponent = portalSlideIn(
    DropdownSlideInContentComponent as <T extends string | number | null>(
        p: DropdownSlideInProps<T>,
    ) => JSX.Element,
) as <T extends string | number | null>(
    props: DropdownSlideInProps<T> & PortalSlideInProps,
) => JSX.Element;

export function DropdownSlideIn<T extends string | number | null>(
    props: DropdownSlideInProps<T> & PortalSlideInProps,
) {
    return <DropdownSlideInComponent {...props} />;
}

export const DropdownSlideInContent = DropdownSlideInContentComponent;

function ItemsBlock<T extends string | number | null>(p: {
    options: DropdownSlideInOption<T>[];
    selected: T | null;
    onSelected: (id: T) => void;
}) {
    return (
        <div className={css({ background: Colors.white })}>
            {p.options.map(option => {
                const isSelected = option.id === p.selected;
                const selectedStyle = isSelected
                    ? {
                          color: Colors.white,
                          backgroundColor: Colors.blue,
                          '&:hover': {
                              color: Colors.white,
                              backgroundColor: Colors.blue,
                          },
                      }
                    : {};
                return (
                    <div
                        key={`option-${option.id}`}
                        className={css({
                            ...Typo.body,
                            color: Colors.blue,
                            background: Colors.white,
                            position: 'relative',
                            borderBottom: `1px solid ${Colors.rgba(
                                Colors.black,
                                0.08,
                            )}`,
                            ...defaultTransition('background', 500),
                            ...hover({ background: Colors.formHover }),
                            ...selectedStyle,
                        })}
                    >
                        <Clickable
                            className={css({
                                display: 'flex',
                                height: '72px',
                                alignItems: 'center',
                            })}
                            element="div"
                            onClick={p.onSelected.bind(null, option.id)}
                        >
                            {option.display}
                        </Clickable>
                    </div>
                );
            })}
        </div>
    );
}

interface SlideInDisplayStyleProps {
    firstRowText: React.ReactNode;
    secondRowText?: string;
}

export const SlideInDisplay = (p: SlideInDisplayStyleProps) => {
    return (
        <div className={css({ display: 'flex' })}>
            <div
                className={css({
                    margin: '0 24px',
                    minWidth: '35px',
                })}
            >
                {p.firstRowText}
            </div>
            {p.secondRowText && <div>{p.secondRowText}</div>}
        </div>
    );
};
